import { Component, Input, OnInit, forwardRef } from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  UntypedFormControl,
  Validator,
} from '@angular/forms';
import * as _ from 'lodash';

@Component({
  selector: 'text-input',
  template: `
    @if (label != '') {
      <label class="input-label" [attr.for]="id"
        >{{ config.label }}
        @if (!optional) {
          <sup class="asterisk">*</sup>
        }
      </label>
    }
    @if (xDeleteButton && !isDisabled) {
      <span class="x-delete" (click)="deleteField($event)">x</span>
    }
    <input
      type="text"
      id="{{ id }}"
      class="form-control input-text"
      placeholder="{{ placeholder }}"
      [attr.maxlength]="maxlength"
      [value]="data"
      (change)="onChange($event)"
      (keyup)="onChange($event)"
      [disabled]="isDisabled"
      [class]="styleClass"
      [ngClass]="{ error: isInvalid }"
    />

    <div *ngFor="let error of errorMessages">
      @if (isInvalid && errors[error.key]) {
        <div class="error-message">
          {{ error.message }}
        </div>
      }
    </div>
  `,
  styles: [
    `
      .error-message {
        color: red;
      }
    `,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TextInputComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => TextInputComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class TextInputComponent implements ControlValueAccessor, Validator, OnInit {
  newValue: string = '';

  // Component config object
  @Input() config: any;

  public id: string = '';
  public label: string = '';
  public placeholder: string = '';
  public optional: boolean;
  public maxlength: number;
  public isDisabled: boolean = false;
  public styleClass: string = '';
  public xDeleteButton: boolean = true;
  public errorMessages: Object[] = [];
  public errors: Object = {};
  public isInvalid: boolean = false;
  public data: any;

  constructor() {}

  public deleteField(event) {
    this.data = '';
    this.propagateChange(this.data);
  }

  // this is the initial value set to the component
  public writeValue(obj: any) {
    this.data = obj;
  }

  // registers 'fn' that will be fired wheb changes are made
  // this is how we emit the changes back to the form
  public registerOnChange(fn: any) {
    this.propagateChange = fn;
  }

  // validates the form, returns null when valid else the validation object
  // in this case we're checking if the json parsing has passed or failed from the onChange method
  public validate(c: UntypedFormControl) {
    setTimeout(() => {
      this.isInvalid = c.invalid && c.dirty;
      this.errors = c.errors;
    }, 0);

    return null;
  }

  // not used, used for touch input
  public registerOnTouched() {}

  // change events from the text input
  public onChange(event) {
    if (!this.isDisabled) {
      // get value from input
      this.newValue = event.target.value;
      this.data = this.newValue;

      // update the form
      this.propagateChange(this.data);
    }
  }

  // the method set in registerOnChange to emit changes back to the form
  private propagateChange = (_: any) => {};

  ngOnInit() {
    let config: any = this.config;

    if (_.isUndefined(config.id) || config.id === '') {
      throw new Error('VASS core: id is required');
    } else {
      this.label = _.isUndefined(config.label) ? '' : config.label;
      this.placeholder = _.isUndefined(config.placeholder) ? '' : config.placeholder;
      this.optional = _.isUndefined(config.optional) ? false : config.optional;
      this.maxlength = _.isUndefined(config.maxlength) ? 9999 : config.maxlength;
      this.isDisabled = _.isUndefined(config.isDisabled) ? false : config.isDisabled;
      this.xDeleteButton = _.isUndefined(config.xDeleteButton)
        ? true
        : config.xDeleteButton;
      this.errorMessages = _.isUndefined(config.errorMessages)
        ? []
        : config.errorMessages;
    }
  }
}
